import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  IconButton,
  Box
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Recycling as RecyclingIcon
} from '@mui/icons-material';
import { Verwertungsnachweis } from '../../types/fahrzeug';

interface VerwertungsnachweisCardProps {
  verwertungsnachweis: Verwertungsnachweis | null;
  onEdit: () => void;
  onDelete?: () => void;
}

const VerwertungsnachweisCard: React.FC<VerwertungsnachweisCardProps> = ({
  verwertungsnachweis,
  onEdit,
  onDelete
}) => {
  // Format date for display
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Nicht angegeben';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Format Merkmal code to readable text
  const formatMerkmal = (merkmal: string | null | undefined): string => {
    switch (merkmal) {
      case '0':
        return '0 - Verwertungsnachweis lag vor';
      case '1':
        return '1 - Fahrzeug wurde nicht als Abfall entsorgt';
      case '2':
        return '2 - Fahrzeug wurde zur Verwertung ins Ausland verbracht';
      default:
        return merkmal || 'Nicht angegeben';
    }
  };

  // Format country code to readable country name (ISO 3166-1 Alpha-2, außer D für Deutschland)
  const formatCountry = (code: string | null | undefined): string => {
    const countries: Record<string, string> = {
      // Deutschland mit Sondercode D
      'D': 'Deutschland',
      // EU-Länder (ISO 3166-1 Alpha-2)
      'AT': 'Österreich',
      'BE': 'Belgien',
      'BG': 'Bulgarien',
      'HR': 'Kroatien',
      'CY': 'Zypern',
      'CZ': 'Tschechien',
      'DK': 'Dänemark',
      'EE': 'Estland',
      'FI': 'Finnland',
      'FR': 'Frankreich',
      'GR': 'Griechenland',
      'HU': 'Ungarn',
      'IE': 'Irland',
      'IT': 'Italien',
      'LV': 'Lettland',
      'LT': 'Litauen',
      'LU': 'Luxemburg',
      'MT': 'Malta',
      'NL': 'Niederlande',
      'PL': 'Polen',
      'PT': 'Portugal',
      'RO': 'Rumänien',
      'SK': 'Slowakei',
      'SI': 'Slowenien',
      'ES': 'Spanien',
      'SE': 'Schweden',
      // Weitere Länder (ISO 3166-1 Alpha-2)
      'AL': 'Albanien',
      'AD': 'Andorra',
      'AM': 'Armenien',
      'AU': 'Australien',
      'AZ': 'Aserbaidschan',
      'BA': 'Bosnien und Herzegowina',
      'BR': 'Brasilien',
      'BY': 'Belarus',
      'CA': 'Kanada',
      'CH': 'Schweiz',
      'CN': 'China',
      'GB': 'Großbritannien',
      'GE': 'Georgien',
      'IN': 'Indien',
      'IS': 'Island',
      'JP': 'Japan',
      'XK': 'Kosovo',
      'MD': 'Moldawien',
      'MX': 'Mexiko',
      'MK': 'Nordmazedonien',
      'ME': 'Montenegro',
      'NO': 'Norwegen',
      'NZ': 'Neuseeland',
      'AR': 'Argentinien',
      'KR': 'Südkorea',
      'RU': 'Russland',
      'RS': 'Serbien',
      'TR': 'Türkei',
      'UA': 'Ukraine',
      'US': 'USA',
      'ZA': 'Südafrika'
    };
    if (!code) return 'Nicht angegeben';
    return countries[code] ? `${countries[code]} (${code})` : code;
  };

  const hasData = verwertungsnachweis && (
    verwertungsnachweis.betriebsnummerDemontagebetrieb ||
    verwertungsnachweis.datumAusstellung ||
    verwertungsnachweis.merkmalVerwertungsnachweis ||
    verwertungsnachweis.staatVerwertung
  );

  return (
    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        avatar={<RecyclingIcon />}
        title="Verwertungsnachweis"
        subheader="Daten zur Fahrzeugverwertung"
        action={
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton 
              onClick={onEdit}
              size="small"
              color="primary"
              title="Bearbeiten"
            >
              <EditIcon />
            </IconButton>
            {hasData && onDelete && (
              <IconButton 
                onClick={onDelete}
                size="small"
                color="error"
                title="Löschen"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        }
      />
      <CardContent>
        {hasData ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Betriebsnummer Demontagebetrieb:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {verwertungsnachweis?.betriebsnummerDemontagebetrieb || 'Nicht angegeben'}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Ausstellungsdatum:
              </Typography>
              <Typography variant="body1">
                {formatDate(verwertungsnachweis?.datumAusstellung)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                Staat der Verwertung:
              </Typography>
              <Typography variant="body1">
                {formatCountry(verwertungsnachweis?.staatVerwertung)}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Merkmal Verwertungsnachweis:
              </Typography>
              <Typography variant="body1">
                {formatMerkmal(verwertungsnachweis?.merkmalVerwertungsnachweis)}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
            Keine Verwertungsnachweisdaten vorhanden. Klicken Sie auf Bearbeiten, um Daten hinzuzufügen.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default VerwertungsnachweisCard;
